"use client";

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Sparkles, Wrench, TrendingUp } from "lucide-react";

import { useTranslation } from "react-i18next";

import { Header, Footer } from "@/components/common";
import { APP_LABELS, TYPE_LABELS, resolveLocalized } from "@/lib/changelog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "loyalty_referrals", label: "Loyalty & Referrals" },
  { key: "product_options", label: "Product Options" },
  { key: "ai_seo", label: "AI SEO" },
];

const APP_ALIASES = {
  "loyalty-rewards": "loyalty_referrals",
  "loyalty-referrals": "loyalty_referrals",
  "product-options": "product_options",
  "ai-seo": "ai_seo",
};

const QUERY_PARAM = "app";

function normalizeFilter(raw) {
  if (!raw) return null;
  const value = String(raw).toLowerCase();
  if (value === "all") return "all";
  if (APP_ALIASES[value]) return APP_ALIASES[value];
  if (FILTERS.some((f) => f.key === value)) return value;
  return null;
}

const APP_DOT = {
  loyalty_referrals: "bg-emerald-400",
  product_options: "bg-amber-400",
  ai_seo: "bg-violet-400",
};

const TYPE_STYLES = {
  fix: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  new: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  improvement: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

const TYPE_ICONS = {
  fix: Wrench,
  new: Sparkles,
  improvement: TrendingUp,
};

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function ChangelogPageClient({ initialEntries = [] }) {
  const { i18n } = useTranslation();
  const locale = (i18n.language || "en").toLowerCase();
  // Seeded from the server render so the list is present on first paint; the
  // refetch below still runs to pick up anything published since.
  const [entries, setEntries] = useState(initialEntries);
  const [loading, setLoading] = useState(initialEntries.length === 0);
  const [active, setActive] = useState("all");
  const [activeTab, setActiveTab] = useState("changelog");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const apply = () => {
      const params = new URLSearchParams(window.location.search);
      const next = normalizeFilter(params.get(QUERY_PARAM));
      setActive(next || "all");
    };
    apply();
    window.addEventListener("popstate", apply);
    return () => window.removeEventListener("popstate", apply);
  }, []);

  useEffect(() => {
    // The server already rendered the list. Refetching would swap the whole
    // list for an equivalent one after paint, reflowing everything below it —
    // that re-render, not the initial load, was the 0.44 CLS.
    if (initialEntries.length > 0) return undefined;

    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch("/api/changelog?limit=100");
        const json = await res.json();
        if (!cancelled && json.success) setEntries(json.data || []);
      } catch (err) {
        console.error("Failed to load changelog", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [initialEntries.length]);

  const filtered = useMemo(() => {
    if (active === "all") return entries;
    return entries.filter((e) => e.app === active);
  }, [entries, active]);

  return (
    <div className="min-h-screen bg-[#212121]">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />

      <section className="relative bg-[#212121] pt-28 pb-8">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-medium tracking-tight text-[#F8F7F4] mb-6">
              What&apos;s new
            </h1>
            <p className="text-lg leading-relaxed text-[#A4A4A4] max-w-2xl">
              Updates and releases across the Keystone app suite. Filter by app
              to see what&apos;s shipping in the one you care about.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const isActive = active === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => {
                      setActive(f.key);
                      if (typeof window !== "undefined") {
                        const url = new URL(window.location.href);
                        if (f.key === "all") {
                          url.searchParams.delete(QUERY_PARAM);
                        } else {
                          url.searchParams.set(QUERY_PARAM, f.key);
                        }
                        window.history.replaceState(null, "", url.toString());
                      }
                    }}
                    className={`rounded-full px-5 py-2 text-sm font-medium transition-all border ${
                      isActive
                        ? "bg-[#F8F7F4] text-[#212121] border-[#F8F7F4]"
                        : "bg-transparent text-[#A4A4A4] border-[#3a3a3a] hover:text-[#F8F7F4] hover:border-[#5a5a5a]"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <main className="container mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#F8F7F4]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-[#A4A4A4]">
            No updates yet for this app.
          </div>
        ) : (
          <div className="divide-y divide-[#2c2c2c]">
            {filtered.map((entry, idx) => {
              const TypeIcon = TYPE_ICONS[entry.type] || Sparkles;
              const { title, bullets } = resolveLocalized(entry, locale);
              return (
                <motion.article
                  key={entry.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.3) }}
                  className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 py-10"
                >
                  <div className="flex md:flex-col md:items-start items-center gap-3">
                    <span className="inline-flex items-center justify-center rounded-md bg-[#2c2c2c] px-3 py-1 text-sm font-medium text-[#F8F7F4]">
                      {entry.version}
                    </span>
                    <span className="text-sm text-[#A4A4A4]">
                      {formatDate(entry.releaseDate)}
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3 text-sm text-[#A4A4A4]">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          APP_DOT[entry.app] || "bg-[#A4A4A4]"
                        }`}
                      />
                      <span>{APP_LABELS[entry.app] || entry.app}</span>
                    </div>

                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h2 className="text-2xl md:text-3xl font-medium text-[#F8F7F4] leading-tight">
                        {title}
                      </h2>
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${
                          TYPE_STYLES[entry.type] ||
                          "bg-[#2c2c2c] text-[#A4A4A4] border-[#3a3a3a]"
                        }`}
                      >
                        <TypeIcon className="w-3 h-3" />
                        {TYPE_LABELS[entry.type] || entry.type}
                      </span>
                    </div>

                    <ul className="space-y-2 text-[#C4C4C4] leading-relaxed">
                      {bullets?.map((b, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-2 inline-block h-1 w-1 rounded-full bg-[#A4A4A4] shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {entry.image && (
                      <div className="mt-6 relative aspect-[16/9] w-full overflow-hidden rounded-xl border border-[#2c2c2c] bg-[#1a1a1a]">
                        <Image
                          src={entry.image}
                          alt={title}
                          fill
                          sizes="(min-width: 768px) 720px, 100vw"
                          className="object-cover"
                        />
                      </div>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
